import type { VoidFunctionComponent } from 'react';
import type Stripe from 'stripe-latest';

import { window } from '../../../../globals';
import Card from '../../../generic/Card/Card';
import Flex from '../../../generic/Flex';
import { BUSINESS_TIER_FEATURES } from '../../billing/consts';
import { SubscriptionBanner } from '../banners';
import FeaturesComponent, { EnterpriseFeaturesComponent } from '../Features';
import LimitsComponent, { LimitsComponentForStaticPlan } from '../Limits';
import {
  getCurrentPlanType,
  getDynamicSubscriptionPlanName,
  getSubscriptionPlanDescription,
  getSubscriptionPlanName,
  planIcon,
} from '../utils';
import type { FossaSubscriptionPlan } from '../../../../types';

import styles from '../style.module.less';

type PlanDetailsProps = {
  data: FossaSubscriptionPlan,
  freePrice: Stripe.Price,
};

const PlanDetails: VoidFunctionComponent<PlanDetailsProps> = ({
  data,
  freePrice,
}) => {
  return (
    <Card
      isFullWidth
      isContentFullHeight
      variant="fill"
      padding="none"
    >
      <Card.Content>
        <Flex direction="row" justify="space-between" align="strech" gap={40}>
          <Flex
            direction="column"
            justify="flex-start"
            align="center"
            gap={32}
            basis="50%"
            className={styles.currentPlanProfileContainer}>
            <DisplayPlanNameAndDescription data={data} freePrice={freePrice} />
          </Flex>
          <Flex
            direction="column"
            justify="flex-start"
            align="center"
            basis="50%"
            className={styles.currentPlanLimitsContainer}>
            <Flex direction="row" justify="space-between" align="flex-start" gap={32}>
              <DisplayPlanLimitsAndFeatures data={data} freePrice={freePrice} />
            </Flex>
          </Flex>
        </Flex>
      </Card.Content>
    </Card>
  );
};

const DisplayPlanNameAndDescription: VoidFunctionComponent<PlanDetailsProps> = ({
  data,
  freePrice,
}) => {
  const org = window?.user?.organization;
  if (!org) {
    return null;
  }
  // Special handling for organizations with custom billing enabled
  const type = org.custom_billing ? 'ENTERPRISE' : getCurrentPlanType(org);
  const icon = planIcon(type);
  const description = getSubscriptionPlanDescription(data);

  const name = type === 'FREE'
    ? getDynamicSubscriptionPlanName(freePrice?.product as Stripe.Product)
    : getSubscriptionPlanName(data);

  return (
    <div className="container">
      <Flex direction="column" gap={24} align="flex-start">
        <img src={icon} alt={data.plan} />
        <Flex direction="column" gap={8} align="flex-start">
          <div className={styles.currentPlanDetailsName}>{name}</div>
          <div className={styles.currentPlanDetailsDescription}>{description}</div>
        </Flex>
        <SubscriptionBanner data={data} />
      </Flex>
    </div>
  );
};

const DisplayPlanLimitsAndFeatures: VoidFunctionComponent<PlanDetailsProps> = ({
  data,
  freePrice,
}) => {
  const org = window?.user?.organization;
  if (!org) {
    return null;
  }
  const planType = getCurrentPlanType(org);
  if (data.custom_billing || planType === 'ENTERPRISE') {
    return (
      <EnterpriseFeaturesComponent />
    );
  }

  if (planType === 'FREE') {
    return (
      <>
        <LimitsComponent product={freePrice.product as Stripe.Product} />
        <FeaturesComponent product={freePrice.product as Stripe.Product} />
      </>
    );
  }

  if (data.price?.product) {
    return (
      <>
        <LimitsComponent product={data.price.product as Stripe.Product} />
        <FeaturesComponent product={data.price.product as Stripe.Product} />
      </>
    );
  }

  let list: string[] = [] as string[];
  switch (planType) {
    case 'BUSINESS':
      list = BUSINESS_TIER_FEATURES;
      break;
    default:
      break;
  }

  return (<LimitsComponentForStaticPlan list={list} />);
};

export default PlanDetails;
